import { useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent,
  IconButton, MenuItem, Paper, Stack, TextField,
} from "@mui/material";
import { StatusPill } from "@/components/StatusPill";
import { PageHeader } from "@/components/PageHeader";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import AddIcon from "@mui/icons-material/Add";
import AutoRenewIcon from "@mui/icons-material/Autorenew";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cssdTraysApi, type CssdCycleStatus, type CssdTrayDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { formatDateTime } from "@/shared/datetime";

const CYCLE_STATUSES: CssdCycleStatus[] = ["InUse", "Sterilizing", "Sterile", "Quarantined"];

/** UC-FN-HOS-03 — CSSD tray register + sterilization cycle log. */
export function CssdTraysPage() {
  const qc = useQueryClient();
  const canManage = useAuth(s => s.hasPermission("hospital.cssd.manage"));

  const [newOpen, setNewOpen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [contents, setContents] = useState("");

  const [cycleTray, setCycleTray] = useState<CssdTrayDto | null>(null);
  const [cycleStatus, setCycleStatus] = useState<CssdCycleStatus>("Sterilizing");
  const [cycleNotes, setCycleNotes] = useState("");

  const query = useQuery({
    queryKey: ["cssd-trays"],
    queryFn: () => cssdTraysApi.list({ pageSize: 200 }),
  });

  const createMut = useMutation({
    meta: { successMessage: "CSSD tray created" },
    mutationFn: () => cssdTraysApi.create({ code, name, contents: contents || undefined }),
    onSuccess: () => {
      setNewOpen(false); setCode(""); setName(""); setContents("");
      qc.invalidateQueries({ queryKey: ["cssd-trays"] });
    },
  });

  const cycleMut = useMutation({
    mutationFn: () => cssdTraysApi.addCycle(cycleTray!.id, { status: cycleStatus, notes: cycleNotes || undefined }),
    onSuccess: () => {
      setCycleTray(null); setCycleNotes(""); setCycleStatus("Sterilizing");
      qc.invalidateQueries({ queryKey: ["cssd-trays"] });
    },
  });

  const columns: GridColDef<CssdTrayDto>[] = [
    { field: "code", headerName: "Code", width: 130 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
    { field: "contents", headerName: "Contents", flex: 1, minWidth: 200 },
    { field: "currentStatus", headerName: "Status", width: 130,
      renderCell: (p) => (
        <StatusPill label={p.value as string} />
      ) },
    { field: "cycleCount", headerName: "Cycles", width: 90, type: "number" },
    { field: "lastCycleAt", headerName: "Last cycle", width: 170,
      valueFormatter: (v) => v ? formatDateTime(v as string) : "—" },
    { field: "actions", headerName: "", width: 60, sortable: false, filterable: false,
      renderCell: (p) => canManage ? (
        <IconButton size="small" onClick={() => setCycleTray(p.row)}>
          <AutoRenewIcon fontSize="small" />
        </IconButton>
      ) : null },
  ];

  return (
    <Box>
      <PageHeader breadcrumbs={["Hospital"]} title="CSSD trays"
        actions={<>{canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)}>
            New tray
          </Button>
        )}</>}
      />
      <Paper sx={{ p: 2 }}>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []} columns={columns} loading={query.isLoading}
            disableRowSelectionOnClick getRowId={(row) => row.id}
          />
        </Box>
      </Paper>

      <Dialog open={newOpen} onClose={() => setNewOpen(false)} fullWidth maxWidth="sm">
        <ClosableDialogTitle onClose={() => setNewOpen(false)}>New CSSD tray</ClosableDialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Code" value={code} required size="small"
              onChange={(e) => setCode(e.target.value)} />
            <TextField label="Name" value={name} required size="small"
              onChange={(e) => setName(e.target.value)} />
            <TextField label="Contents" value={contents} size="small" multiline minRows={2}
              onChange={(e) => setContents(e.target.value)} />
            {createMut.isError && <Alert severity="error">{errorMessage(createMut.error)}</Alert>}
            {(!code || !name) && (
              <Alert severity="info">{!code ? "Code is required." : "Name is required."}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => createMut.mutate()}
            disabled={!code || !name || createMut.isPending}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!cycleTray} onClose={() => setCycleTray(null)} fullWidth maxWidth="sm">
        <ClosableDialogTitle onClose={() => setCycleTray(null)}>Add sterilization cycle — {cycleTray?.code}</ClosableDialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="New status" value={cycleStatus} size="small"
              onChange={(e) => setCycleStatus(e.target.value as CssdCycleStatus)}>
              {CYCLE_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField label="Notes" value={cycleNotes} size="small" multiline minRows={2}
              onChange={(e) => setCycleNotes(e.target.value)} />
            {cycleMut.isError && <Alert severity="error">{errorMessage(cycleMut.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCycleTray(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => cycleMut.mutate()}
            disabled={cycleMut.isPending}>Log cycle</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
